import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaWater,
  FaMapMarkerAlt,
  FaRegBuilding,
  FaCheckCircle,
  FaPhoneAlt,
  FaClipboardList,
  FaFlag,
  FaTint,
} from "react-icons/fa";
import { MapContainer, TileLayer, Circle, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./WaterResources.css";

const schemes = [
  {
    title: "Mission Kakatiya",
    description:
      "A water restoration program for sustainable water management in Telangana, aiming to rejuvenate over 46,000 tanks and lakes.",
    eligibility: "All Farmers",
    contact: "District Water Authority – Warangal",
  },
  {
    title: "Kaleshwaram Lift Irrigation Project",
    description:
      "Major lift irrigation scheme designed to irrigate 18 lakh hectares across 13 districts in Telangana.",
    eligibility: "Eligible Registered Farmers",
    contact: "Irrigation Dept – Karimnagar Office",
  },
  {
    title: "Rythu Bandhu Water Scheme",
    description:
      "Government scheme to subsidize borewell construction, promoting efficient use of underground water resources.",
    eligibility: "Marginal & Small Farmers",
    contact: "Agriculture Department – Village Level Office",
  },
];



const WaterResources = () => {
  const [waterSources, setWaterSources] = useState([]);
  const [allWaterSources, setAllWaterSources] = useState([]); // Store all sources
  const [selectedStateFilter, setSelectedStateFilter] = useState('all'); // Filter state
  const [availableStates, setAvailableStates] = useState([]); // List of states
  const [userState, setUserState] = useState(null);
  const [userDistrict, setUserDistrict] = useState(null);
  const [userRole, setUserRole] = useState('farmer');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get user's data from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role || 'farmer');
        setUserState(user.selectedState || null);
        setUserDistrict(user.selectedDistrict || null);
        
        console.log('User logged in:', { 
          role: user.role, 
          state: user.selectedState, 
          district: user.selectedDistrict 
        });
        
        // Farmers can view all resources - no validation needed
        // State/district admins need their location info
        if (user.role === 'state' && !user.selectedState) {
          setError('State information not available');
        } else if (user.role === 'district' && (!user.selectedState || !user.selectedDistrict)) {
          setError('District information not available');
        }
        // If farmer, no error - they can view all
      } catch (error) {
        console.error('Error parsing user data:', error);
        setError('Error loading user data');
      }
    }
  }, []);

  useEffect(() => {
    fetchWaterSources();
  }, []);

  // Filter water sources when state filter changes
  useEffect(() => {
    if (selectedStateFilter === 'all') {
      setWaterSources(allWaterSources);
    } else {
      const filtered = allWaterSources.filter(item => 
        item.statename?.toLowerCase() === selectedStateFilter.toLowerCase()
      );
      setWaterSources(filtered);
    }
  }, [selectedStateFilter, allWaterSources]);

  const validSource = waterSources.find(
    (src) =>
      src.latitude &&
      src.longitude &&
      !isNaN(parseFloat(src.latitude)) &&
      !isNaN(parseFloat(src.longitude))
  );

  useEffect(() => {
    // Farmers can always fetch (no location required)
    // State admins need state
    // District admins need state and district
    if (userRole === 'farmer' || 
        (userRole === 'state' && userState) || 
        (userRole === 'district' && userState && userDistrict)) {
      fetchWaterSources();
    } else {
      setLoading(false);
    }
  }, [userState, userDistrict, userRole]);

  const fetchWaterSources = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.get(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/water-sources`);
      let data = res.data;

      console.log(`Total water sources: ${data.length}`);

      // Filter data based on user role
      if (userRole === 'state') {
        // State admins see all resources in their state
        data = data.filter((item) => {
          const itemState = item.statename?.toLowerCase().trim();
          const userStateLower = userState?.toLowerCase().trim();
          return itemState === userStateLower;
        });
        console.log(`Filtered to ${data.length} sources for state: ${userState}`);
      } else if (userRole === 'district') {
        // District admins see resources in their district
        data = data.filter((item) => {
          const itemDistrict = item.district?.toLowerCase().trim();
          const itemState = item.statename?.toLowerCase().trim();
          const userDistrictLower = userDistrict?.toLowerCase().trim();
          const userStateLower = userState?.toLowerCase().trim();
          
          return (
            itemDistrict === userDistrictLower && 
            itemState === userStateLower
          );
        });
        console.log(`Filtered to ${data.length} sources for district: ${userDistrict}, ${userState}`);
      }
      // For farmers (userRole === 'farmer'), show ALL data - no filtering

      setWaterSources(data);
      console.log(`Displaying ${data.length} water sources for ${userRole}`);
    } catch (err) {
      console.error("Failed to fetch water sources:", err);
      setError('Failed to load water resources data');
    } finally {
      setLoading(false);
    }
  };

  // Show error state if user is not logged in
  if (error) {
    return (
      <div
        style={{
          fontFamily: "Poppins, sans-serif",
          backgroundColor: "#f4faff",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: "#fff",
            padding: "40px",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <FaWater style={{ fontSize: "48px", color: "#006994", marginBottom: "20px" }} />
          <h2 style={{ color: "#004d73", marginBottom: "15px" }}>Access Required</h2>
          <p style={{ color: "#666", fontSize: "16px" }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: "Poppins, sans-serif",
        backgroundColor: "#f4faff",
        minHeight: "100vh",
      }}
    >
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* HEADER */}
      <div
        className="water-resources-header"
        style={{
          background: "linear-gradient(90deg, #006994 0%, #004d73 100%)",
          color: "white",
          padding: "40px 20px",
          textAlign: "center",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        <h1
          style={{
            fontSize: "40px",
            marginBottom: "8px",
            fontWeight: "700",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <FaWater /> Water Resources - {userRole === 'state' ? userState : userRole === 'district' ? userDistrict : 'All India'}
        </h1>
        <p
          style={{
            fontSize: "18px",
            opacity: "0.9",
            maxWidth: "650px",
            margin: "auto",
          }}
        >
          Discover <strong>verified water resources</strong> and{" "}
          <strong>government schemes</strong> available{" "}
          {userRole === 'state' 
            ? `in ${userState} state` 
            : userRole === 'district'
            ? `in ${userDistrict}, ${userState}`
            : 'across India'}
        </p>
      </div>

      {/* DISTRICT INFO BANNER */}
      <div className="water-location-banner" style={{ 
        textAlign: "center", 
        padding: "20px",
        background: "linear-gradient(135deg, #e3f2fd 0%, #f1f8e9 100%)",
        margin: "0 20px",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
      }}>
        <div className="water-location-badge" style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "12px",
          background: "#fff",
          padding: "12px 24px",
          borderRadius: "25px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)"
        }}>
          <FaMapMarkerAlt style={{ color: "#006994", fontSize: "18px" }} />
          <span style={{ 
            fontSize: "16px", 
            fontWeight: "600", 
            color: "#004d73" 
          }}>
            Showing resources for: {userRole === 'state' 
              ? `${userState} state` 
              : userRole === 'district'
              ? `${userDistrict}, ${userState}`
              : 'All India'}
          </span>
        </div>

        {/* State Filter Dropdown - Only show for farmers */}
        {userRole === 'farmer' && (
          <div className="water-filter-row" style={{ 
            marginTop: "15px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}>
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#004d73" 
            }}>
              Filter by State:
            </label>
            <select
              value={selectedStateFilter}
              onChange={(e) => setSelectedStateFilter(e.target.value)}
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                border: "2px solid #006994",
                borderRadius: "8px",
                backgroundColor: "white",
                color: "#004d73",
                cursor: "pointer",
                minWidth: "200px"
              }}
            >
              <option value="all">All States</option>
              {availableStates.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            <span style={{ fontSize: "14px", color: "#666" }}>
              ({waterSources.length} resources)
            </span>
          </div>
        )}

        {loading && (
          <div style={{ marginTop: "15px", color: "#666" }}>
            <div style={{ 
              display: "inline-block", 
              width: "20px", 
              height: "20px", 
              border: "2px solid #006994", 
              borderTop: "2px solid transparent", 
              borderRadius: "50%", 
              animation: "spin 1s linear infinite",
              marginRight: "10px"
            }}></div>
            Loading water resources...
          </div>
        )}
      </div>

      {/* WATER SOURCES */}
      <div style={{ padding: "0 20px" }}>
        <h2
          style={{
            color: "#004d73",
            marginBottom: "15px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <FaMapMarkerAlt /> Water Sources {userRole === 'state' ? `in ${userState}` : userRole === 'district' ? `in ${userDistrict}` : 'Across India'}
        </h2>
        {loading ? (
          <div style={{ 
            textAlign: "center", 
            padding: "40px", 
            color: "#666" 
          }}>
            <div style={{ 
              display: "inline-block", 
              width: "30px", 
              height: "30px", 
              border: "3px solid #006994", 
              borderTop: "3px solid transparent", 
              borderRadius: "50%", 
              animation: "spin 1s linear infinite",
              marginBottom: "15px"
            }}></div>
            <p>Loading water sources for your district...</p>
          </div>
        ) : waterSources.length === 0 ? (
          <div style={{ 
            background: "#fff3cd", 
            border: "1px solid #ffeaa7", 
            borderRadius: "8px", 
            padding: "20px", 
            textAlign: "center",
            marginBottom: "20px"
          }}>
            <FaWater style={{ fontSize: "32px", color: "#856404", marginBottom: "10px" }} />
            <p style={{ color: "#856404", fontSize: "16px", margin: "0" }}>
              No water sources found {userRole === 'state' 
                ? `for ${userState} state` 
                : userRole === 'district'
                ? `for ${userDistrict}, ${userState}`
                : 'in the database'}
            </p>
            <p style={{ color: "#856404", fontSize: "14px", margin: "5px 0 0 0" }}>
              {userRole === 'farmer' 
                ? 'The water resources data is being updated. Please check back later.'
                : `This could mean the data is not yet available for your ${userRole === 'state' ? 'state' : 'district'}.`}
            </p>
          </div>
        ) : (
          <div
            className="water-resources-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "20px",
              marginBottom: "40px",
            }}
          >
            {waterSources.map((src, index) => (
              <div
                key={index}
                style={{
                  background: "#fff",
                  padding: "18px",
                  borderRadius: "12px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  transition: "transform 0.3s ease",
                  border: "1px solid #e3f2fd",
                }}
              >
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#004d73",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "8px",
                  }}
                >
                  <FaRegBuilding /> {src.officename || "Unknown Office"}
                </h3>
                <p style={{ fontSize: "14px", color: "#444", margin: "3px 0" }}>
                  <FaMapMarkerAlt color="#006994" /> <strong>District:</strong>{" "}
                  {src.district || "Unknown"}
                </p>
                <p style={{ fontSize: "14px", color: "#444", margin: "3px 0" }}>
                  <FaFlag color="#006994" /> <strong>State:</strong>{" "}
                  {src.statename || "Unknown"}
                </p>
                <p style={{ fontSize: "14px", color: "#444", margin: "3px 0" }}>
                  <strong>Pincode:</strong> {src.pincode || "Unknown"}
                </p>
                {src.latitude && src.longitude && (
                  <p
                    style={{ fontSize: "14px", color: "#444", margin: "3px 0" }}
                  >
                    <FaTint color="#004d73" /> <strong>Coordinates:</strong>{" "}
                    {src.latitude}, {src.longitude}
                  </p>
                )}
                <div
                  style={{
                    backgroundColor: "#e6f7f1",
                    color: "#2e7d32",
                    fontSize: "12px",
                    padding: "4px 10px",
                    borderRadius: "20px",
                    fontWeight: "500",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    marginTop: "6px",
                  }}
                >
                  <FaCheckCircle /> Verified Resource
                </div>
                <button
                  style={{
                    marginTop: "10px",
                    padding: "8px 12px",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#007e5b",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                  onClick={() =>
                    alert(
                      `Contact info not available for office ${
                        src.officename || "Unknown"
                      }`
                    )
                  }
                >
                  <FaPhoneAlt /> Contact Office
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MAP SECTION */}
      {waterSources.length > 0 && (
        <div
          style={{
            maxWidth: "1100px",
            margin: "30px auto",
            background: "white",
            padding: "15px",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <h2
            style={{
              color: "#004d73",
              marginBottom: "10px",
              borderBottom: "2px solid #006994",
              paddingBottom: "8px",
            }}
          >
            🗺 Water Sources Map {userRole === 'state' ? `- ${userState}` : userRole === 'district' ? `- ${userDistrict}` : '- India'}
          </h2>
          <MapContainer
            center={
              validSource
                ? [
                    parseFloat(validSource.latitude),
                    parseFloat(validSource.longitude),
                  ]
                : [20.5937, 78.9629] // default India center
            }
            zoom={5}
            style={{ height: "400px", borderRadius: "8px" }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {waterSources
              .filter(
                (src) =>
                  src.latitude &&
                  src.longitude &&
                  !isNaN(parseFloat(src.latitude)) &&
                  !isNaN(parseFloat(src.longitude))
              )
              .map((src, index) => (
                <Circle
                  key={index}
                  center={[parseFloat(src.latitude), parseFloat(src.longitude)]}
                  radius={5000}
                  pathOptions={{ color: "#006994", fillOpacity: 0.4 }}
                >
                  <Popup>
                    <strong>{src.officename || "Unknown Office"}</strong>
                    <br />
                    📍 {src.district}, {src.statename}
                  </Popup>
                </Circle>
              ))}
          </MapContainer>
        </div>
      )}

      {/* GOVERNMENT SCHEMES */}
      <div style={{ padding: "20px" }}>
        <h2
          style={{
            color: "#004d73",
            marginTop: "30px",
            marginBottom: "15px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <FaClipboardList /> Government Water Schemes
        </h2>
        {schemes.map((scheme, i) => (
          <div
            key={i}
            style={{
              background: "#fdfcff",
              padding: "16px",
              borderRadius: "10px",
              boxShadow: "0 3px 8px rgba(0,0,0,0.05)",
              marginBottom: "15px",
              borderLeft: "5px solid #006994",
            }}
          >
            <h4
              style={{
                fontWeight: "bold",
                fontSize: "16px",
                color: "#006994",
                marginBottom: "6px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {scheme.title}
            </h4>
            <p style={{ fontSize: "14px", marginBottom: "5px", color: "#333" }}>
              {scheme.description}
            </p>
            <p style={{ fontSize: "13px", color: "#555" }}>
              <strong>Eligibility:</strong> {scheme.eligibility}
            </p>
            <p style={{ fontSize: "13px", color: "#555" }}>
              <strong>Contact:</strong> {scheme.contact}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WaterResources;